/**
 * assemblyHighlight.js — E8 (historia 152, §8.8)
 *
 * Pure, framework-free resolution of CA2.3 (`RN-02.6`/`RN-02.8`): given the assembly selected in
 * the BOM TREE and the workflow as it stands in the editor, which graph nodes and which edges the
 * highlight covers.
 *
 * Decisión de diseño nº 4 (§6.2): this is a CARDINALITY extension of the highlight #28 already
 * has, never a second mechanism — the job assignments come from `bomTree.js#collectJobAssignments`
 * untouched, the target node of each one from `resolveHighlightTargetNodeId` (so jobs nested in a
 * subworkflow that is not open collapse into their container node, exactly like the chips), and
 * the painting is done by `bomTree.js#applyJobHighlight`, which now takes a `Set`.
 *
 * The two halves live apart on purpose: the BOM tree holds the COMPONENTS (so it resolves the node
 * ids and can declare "no operations consume this assembly"), while the editor holds the EDGES of
 * the rendered graph (so it resolves the edge ids). Nobody holds both.
 *
 * Nothing here writes: HU-02 persists no data and never touches the editor model (`IN14`).
 */
import { collectJobAssignments, getJobAssignmentsFor, normalizeBomPosition, resolveHighlightTargetNodeId } from './bomTree'

/**
 * S69 (RN-02.6 point 1): every component of the assembly's subtree — the assembly itself and its
 * descendants at ANY depth — by normalized `position` (`"1.2.0"` ≡ `"1.2"`, the tree's own
 * criterion). Descending is a matter of position prefix, so a grandchild counts exactly like a
 * child.
 * @param {string} assemblyPosition The selected assembly's `position`
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[]} components
 * The workflow's BOM, flat
 * @returns {string[]} `workflowResourceUUID` of the assembly and of every descendant
 */
const collectAssemblyComponentUUIDs = (assemblyPosition, components) => {
  const root = normalizeBomPosition(assemblyPosition)
  if (!root) {
    return []
  }

  return (components || [])
    .filter((component) => {
      const position = normalizeBomPosition(component?.position)
      return position === root || position.startsWith(root + '.')
    })
    .map((component) => component?.uuid)
    .filter(Boolean)
}

/**
 * S69/S70 (RN-02.6 point 1, RN-02.8): the graph nodes the assembly highlights — every operation
 * consuming any component of its subtree, resolved to the node that EXISTS on the current canvas
 * (the job itself, or its container subworkflow node). An assembly nothing consumes resolves an
 * EMPTY set, never every node: the caller declares it with "No operations consume this assembly".
 * @param {Object} params
 * @param {string} params.assemblyPosition The selected assembly's `position`
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[]} params.components The workflow's BOM, flat
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').Node[]} [params.nodes] The live workflow's top-level nodes
 * @param {Object<string, import('./bomTree').JobAssignment[]>} [params.assignmentsByUUID] The
 * assignments already derived by the caller (the BOM view keeps them for its chips); derived from
 * `nodes` when absent, with the same `collectJobAssignments` of #28
 * @returns {Set<string>} Node ids to highlight
 */
const collectAssemblyNodeIds = ({ assemblyPosition, components, nodes = [], assignmentsByUUID }) => {
  const assignments = assignmentsByUUID || collectJobAssignments(nodes)
  const nodeIds = new Set()

  collectAssemblyComponentUUIDs(assemblyPosition, components).forEach((uuid) => {
    getJobAssignmentsFor(uuid, assignments).forEach((assignment) => {
      nodeIds.add(resolveHighlightTargetNodeId(assignment))
    })
  })

  return nodeIds
}

/**
 * S69/S70 (RN-02.6 point 2, `AssemblyHighlight`): the edges BETWEEN the highlighted nodes — an
 * edge enters only if its TWO ends are in the set. One end highlighted is not a connection between
 * two operations of the assembly, and it is not highlighted.
 * @param {import('reactflow').Edge[]} edges Currently rendered reactflow edges
 * @param {Set<string>} nodeIds The highlighted node ids
 * @returns {Set<string>} Edge ids to highlight
 */
const edgesBetween = (edges, nodeIds) => {
  const highlightedNodes = nodeIds instanceof Set ? nodeIds : new Set(nodeIds || [])

  return new Set((edges || [])
    .filter((edge) => highlightedNodes.has(edge?.source) && highlightedNodes.has(edge?.target))
    .map((edge) => edge?.id))
}

export {
  collectAssemblyComponentUUIDs,
  collectAssemblyNodeIds,
  edgesBetween
}
